import {
  transformUrnToWitboostId,
  WitboostDomain,
  WitboostSystem,
} from '@agilelab/plugin-wb-builder-common';
import {
  AsyncEnumFilter,
  createNameWithVersionMap,
  GenericElement,
  NotFoundException,
  Pagination,
  SearchFilter,
  TableCellProps,
  WbResponsiveTableToolbar,
} from '@agilelab/plugin-wb-platform';
import { practiceShaperApiRef } from '@agilelab/plugin-wb-practice-shaper';
import { PART_OF } from '@agilelab/plugin-wb-practice-shaper-common';
import {
  Entity,
  parseEntityRef,
  stringifyEntityRef,
} from '@mainstage/catalog-model';
import { useApi } from '@mainstage/core-plugin-api';
import { CatalogApi, catalogApiRef } from '@mainstage/plugin-catalog-react';
import { FC, ReactNode, useCallback, useMemo, useState } from 'react';
import useAsync from 'react-use/lib/useAsync';
import { contextProviderFactory } from '../scopeProvider.factory';
import { MyProvider, ProviderFilters, ScopeProviderType } from '../types';
import { getLabelAndValue } from '../../utils';

interface ComponentFilters extends ProviderFilters {
  system?: { name: string; ref: string }[];
  componentType?: { name: string; ref: string }[];
  domain?: { name: string; ref: string }[];
}

interface ComponentFilterProps {
  value: { name: string; ref: string }[];
  changeFilters: <K extends keyof ComponentFilters>(
    key: K,
    value: ComponentFilters[K],
  ) => void;
  getOptions: () => Promise<{ name: string; ref: string }[]>;
}

interface ComponentContextType
  extends ScopeProviderType<GenericElement, ProviderFilters> {}

const [useComponentContext, ComponentContextProvider] =
  contextProviderFactory<ComponentContextType>();

export default useComponentContext;

export interface ComponentProviderProps {
  children: ReactNode;
}

const SystemFilter: FC<ComponentFilterProps> = ({
  value,
  changeFilters,
  getOptions,
}) => {
  return (
    <AsyncEnumFilter<{ name: string; ref: string }>
      field="System"
      value={value ?? undefined}
      onChange={system => {
        changeFilters('system', system);
      }}
      renderOption={o => o.name}
      renderValue={v => v.name}
      onSearch={(searchValue, options) =>
        options.filter(option =>
          new RegExp(searchValue, 'ig').test(option.name),
        )
      }
      getOptions={getOptions}
    />
  );
};

const ComponentTypeFilter: FC<ComponentFilterProps> = ({
  value,
  changeFilters,
  getOptions,
}) => {
  return (
    <AsyncEnumFilter<{ name: string; ref: string }>
      field="Component Type"
      value={value}
      onChange={componentType => {
        changeFilters('componentType', componentType);
      }}
      renderOption={o => o.name}
      renderValue={v => v.name}
      onSearch={(searchValue, options) =>
        options.filter(option =>
          new RegExp(searchValue, 'ig').test(option.name),
        )
      }
      getOptions={getOptions}
    />
  );
};

const DomainFilter: FC<ComponentFilterProps> = ({
  value,
  changeFilters,
  getOptions,
}) => {
  return (
    <AsyncEnumFilter<{ name: string; ref: string }>
      field="Domain"
      value={value ?? undefined}
      onChange={domain => {
        changeFilters('domain', domain);
      }}
      renderOption={o => o.name}
      renderValue={v => v.name}
      onSearch={(searchValue, options) =>
        options.filter(option =>
          new RegExp(searchValue, 'ig').test(option.name),
        )
      }
      getOptions={getOptions}
    />
  );
};

export const ComponentFiltersSection = () => {
  const { filters, changeFilters, resetFilters } = useComponentContext();
  const catalogApi = useApi(catalogApiRef);
  const practiceShaperApi = useApi(practiceShaperApiRef);

  const fetchAvailableSystems = useCallback(async () => {
    const domainRefs = filters.domain?.map((d: { ref: string }) => d.ref);
    const allItems: Entity[] = [];
    let cursor: string | undefined;
    do {
      const result = await catalogApi.queryEntities({
        filter: {
          kind: ['System'],
          ...(domainRefs?.length
            ? { [`relations.${PART_OF}`]: domainRefs }
            : {}),
        },
        fields: ['metadata.name', 'metadata.namespace', 'spec.mesh.name'],
        limit: 500,
        cursor,
      });
      allItems.push(...result.items);
      cursor = result.pageInfo.nextCursor;
    } while (cursor);

    return allItems.map(entity => {
      const ref = stringifyEntityRef({
        kind: 'system',
        name: entity.metadata.name,
        namespace: entity.metadata.namespace,
      });
      const system = entity as WitboostSystem;
      return { name: system.spec.mesh.name, ref };
    });
  }, [catalogApi, filters.domain]);

  const fetchAvailableComponentTypes = useCallback(async () => {
    const result = await practiceShaperApi.getComponentTypes({});
    return result.items.map(ct => ({
      name: ct.metadata.displayName ?? ct.metadata.name,
      ref: stringifyEntityRef({
        kind: 'componenttype',
        name: ct.metadata.name,
      }),
    }));
  }, [practiceShaperApi]);

  const fetchAvailableDomains = useCallback(async () => {
    const response = await catalogApi.getEntities({
      filter: { kind: 'domain' },
    });
    return response.items.map(item => {
      const ref = stringifyEntityRef({
        kind: 'domain',
        name: item.metadata.name,
        namespace: item.metadata.namespace,
      });
      const domain = item as WitboostDomain;
      return { name: domain.spec.mesh.name, ref };
    });
  }, [catalogApi]);

  return (
    <WbResponsiveTableToolbar
      onClear={resetFilters}
      style={{ marginTop: '16px' }}
      marginBottom
    >
      <WbResponsiveTableToolbar.Filters>
        <SearchFilter
          key="search"
          value={filters.search ?? ''}
          changeFilters={changeFilters}
        />
        <SystemFilter
          key="system"
          value={filters.system ?? []}
          changeFilters={changeFilters as ComponentFilterProps['changeFilters']}
          getOptions={fetchAvailableSystems}
        />
        <ComponentTypeFilter
          key="componentType"
          value={filters.componentType ?? []}
          changeFilters={changeFilters as ComponentFilterProps['changeFilters']}
          getOptions={fetchAvailableComponentTypes}
        />
        <DomainFilter
          key="domain"
          value={filters.domain ?? []}
          changeFilters={changeFilters as ComponentFilterProps['changeFilters']}
          getOptions={fetchAvailableDomains}
        />
      </WbResponsiveTableToolbar.Filters>
    </WbResponsiveTableToolbar>
  );
};

export const ComponentProvider: FC<ComponentProviderProps> = props => {
  const { children } = props;
  const catalogApi = useApi(catalogApiRef);
  const [filters, setFilters] = useState<ComponentFilters>({});
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [prevCursor, setPrevCursor] = useState<string | undefined>(undefined);
  const [count, setCount] = useState<number>(0);
  const [pagination, setPagination] = useState<Pagination>({
    limit: 25,
    currentPage: 0,
  });
  const defaultColumns: TableCellProps<GenericElement>[] = useMemo(() => {
    return [
      {
        headerName: 'Name',
        field: 'displayName',
        uniqueFieldId: 'displayName',
      },
      {
        headerName: 'Description',
        field: 'description',
        uniqueFieldId: 'description',
      },
    ];
  }, []);

  const componentState = useAsync(async () => {
    // Components don't have spec.domain; resolve domain filter via their systems
    let systemRefsFilter: string[] | undefined;
    const hasSystemFilter = !!filters.system?.length;
    const hasDomainFilter = !!filters.domain?.length;

    if (hasSystemFilter || hasDomainFilter) {
      let systemRefs = filters.system?.map(s => s.ref) ?? [];

      if (hasDomainFilter) {
        const domainRefs = filters.domain!.map(d => d.ref);
        const allSystemItems: Entity[] = [];
        let cursor: string | undefined;
        do {
          const systemsForDomains = await catalogApi.queryEntities({
            filter: { kind: ['System'], [`relations.${PART_OF}`]: domainRefs },
            fields: ['metadata.name', 'metadata.namespace'],
            limit: 500,
            cursor,
          });
          allSystemItems.push(...systemsForDomains.items);
          cursor = systemsForDomains.pageInfo.nextCursor;
        } while (cursor);
        const domainSystemRefs = allSystemItems.map(s =>
          stringifyEntityRef({
            kind: 'system',
            name: s.metadata.name,
            namespace: s.metadata.namespace,
          }),
        );

        if (hasSystemFilter) {
          // Intersect: only systems that are both explicitly selected and in the selected domains
          const domainSet = new Set(domainSystemRefs);
          systemRefs = systemRefs.filter(r => domainSet.has(r));
        } else {
          systemRefs = domainSystemRefs;
        }
      }

      // Empty intersection means no component can satisfy both filters → return nothing
      if (systemRefs.length === 0) {
        setNextCursor(undefined);
        setPrevCursor(undefined);
        setCount(0);
        return [];
      }

      systemRefsFilter = systemRefs;
    }

    const result = await catalogApi.queryEntities({
      filter: {
        kind: ['Component'],
        ...(systemRefsFilter !== undefined
          ? { [`relations.${PART_OF}`]: systemRefsFilter }
          : {}),
        'spec.instanceOf': filters.componentType?.map(ct => ct.ref) || [],
      },
      limit: pagination.limit,
      cursor: pagination.cursor,
      fullTextFilter: {
        term: filters.search!,
        fields: ['spec.mesh.name', 'metadata.name'],
      },
    });
    setNextCursor(result.pageInfo.nextCursor);
    setPrevCursor(result.pageInfo.prevCursor);
    setCount(result.totalItems);

    const adaptedValues = result.items.map(entity => getLabelAndValue(entity));

    const nameWithVersionMap = createNameWithVersionMap(
      adaptedValues,
      'displayName',
    );

    return adaptedValues.map(val => {
      return {
        ...val,
        displayName: nameWithVersionMap[val.id].nameWithVersion,
      };
    });
  }, [filters, pagination.limit, pagination.cursor]);

  const getRowId = (row: any) => row.id;

  return (
    <ComponentContextProvider
      value={
        {
          entitiesState: componentState,
          columns: defaultColumns,
          getRowId,
          filters,
          changeFilters: <K extends keyof ComponentFilters>(
            key: K,
            filterValue: ComponentFilters[K],
          ) => {
            setPagination(p => ({ ...p, currentPage: 0, cursor: undefined }));
            setFilters(f => ({ ...f, [key]: filterValue }));
          },
          resetFilters: () => {
            setFilters({});
          },
          pagination,
          setPagination,
          count,
          nextCursor,
          prevCursor,
        } as unknown as ScopeProviderType<GenericElement, ProviderFilters>
      }
    >
      {children}
    </ComponentContextProvider>
  );
};

export class ComponentHandlerProvider
  implements MyProvider<GenericElement, ProviderFilters>
{
  private readonly catalogApi: CatalogApi;

  constructor(catalogApi: CatalogApi) {
    this.catalogApi = catalogApi;
  }

  useContext = useComponentContext;

  renderContextProvider(children: ReactNode) {
    return <ComponentProvider>{children}</ComponentProvider>;
  }

  filtersSection = () => <ComponentFiltersSection />;

  async resolve(ref: string) {
    const resolvedRef = transformUrnToWitboostId(ref) ?? ref;
    const parsedEntityRef = parseEntityRef(resolvedRef);
    try {
      if (parsedEntityRef.kind === 'component') {
        const entity = await this.catalogApi.getEntityByRef(resolvedRef);
        if (entity) return getLabelAndValue(entity);
        throw new NotFoundException(
          `Cannot find a Component entity with ref: ${ref}`,
        );
      }

      return undefined;
    } catch (error) {
      throw new Error(`Error while resolving entity with ref: ${ref}`);
    }
  }
}
